import { useState, useEffect, useCallback } from 'react';
import { getKnowledgeBaseResponse } from '@/services/data/knowledgeBase';
import type { KnowledgeBaseResponse } from '@/services/data/knowledgeBase';

export function useKnowledgeBaseStatus(userId: string | undefined) {
  const [isComplete, setIsComplete] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const checkKnowledgeBase = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getKnowledgeBaseResponse(userId);
      
      if (!result.success || !result.data) {
        setIsComplete(false);
        return;
      }

      const kb = result.data as KnowledgeBaseResponse;
      
      // Check text fields
      const textFields: (keyof KnowledgeBaseResponse)[] = [
        'objectif',
        'domaines_parole',
        'unique_different'
      ];
      
      const textFieldsFilled = textFields.every(field => {
        const value = kb[field];
        return value && typeof value === 'string' && value.trim().length > 0;
      });
      
      // Check cible (jsonb array)
      const cibleData = kb.cible as string[] | null;
      const cibleFilled = cibleData && Array.isArray(cibleData) && cibleData.some(v => v.trim() !== '');

      const allFieldsFilled = textFieldsFilled && cibleFilled;

      setIsComplete(allFieldsFilled);
    } catch (error) {
      console.error('Error checking knowledge base:', error);
      setIsComplete(false);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkKnowledgeBase();
  }, [checkKnowledgeBase]);

  const refetch = useCallback(() => {
    checkKnowledgeBase();
  }, [checkKnowledgeBase]);

  return { isComplete, isLoading, refetch };
}
